import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import {
    isPatientPreferredPharmacyRoute,
    resolveSpecialMemberHandling,
    SPECIAL_MEMBER_HANDLING_FLAG,
} from 'services/specialMemberHandling';
import { getMemberIdentityFromSource } from 'services/useScopeAssessorMemberDetails';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { useGetMemberAttributesQuery } from 'store/api/scopeAssessorApi';

/**
 * Resolves special-handling tooltip for a referral/treatment member header.
 *
 * @param {object|undefined|null} source Referral or treatment payload
 * @param {{ routeCode?: string, supplierNpi?: string }} [routing]
 * @returns {{ scenario: string, tooltip: string } | null}
 */
export const useSpecialMemberHandling = (source, routing = {}) => {
    const flags = useFlags() ?? {};
    const showSpecialMemberHandling = !!flags[SPECIAL_MEMBER_HANDLING_FLAG];

    const attributeQueryArgs = useMemo(() => {
        if (!showSpecialMemberHandling || !source?.payerId) {
            return skipToken;
        }
        const { memberId, firstName, dateOfBirth } = getMemberIdentityFromSource(source);
        if (!memberId || !firstName || !dateOfBirth) {
            return skipToken;
        }
        return {
            payerId: source.payerId,
            memberId,
            firstName,
            dateOfBirth,
        };
    }, [source, showSpecialMemberHandling]);

    const { data: memberAttributes = [] } = useGetMemberAttributesQuery(attributeQueryArgs);

    const isPreferredPharmacy = isPatientPreferredPharmacyRoute(routing.routeCode);
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        showSpecialMemberHandling && isPreferredPharmacy && routing.supplierNpi
            ? { supplierNpi: routing.supplierNpi }
            : skipToken,
    );

    return useMemo(
        () =>
            showSpecialMemberHandling
                ? resolveSpecialMemberHandling({
                      attributes: memberAttributes,
                      isPreferredPharmacy,
                      preferredPharmacyName: supplierDetails?.organizationName,
                  })
                : null,
        [
            showSpecialMemberHandling,
            memberAttributes,
            isPreferredPharmacy,
            supplierDetails?.organizationName,
        ],
    );
};
